import { useRef, useEffect, useCallback } from 'react';

interface Particle {
  x: number;
  y: number;
  targetX: number;
  targetY: number;
  vx: number;
  vy: number;
  size: number;
  alpha: number;
  life: number;
  maxLife: number;
  color: string;
}

interface SummonParticlesProps {
  color: string;
  active: boolean;
  mode: 'converge' | 'burst' | 'ambient';
  intensity?: number;
}

const MAX_PARTICLES = 200;

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return { r: 255, g: 255, b: 255 };
  return {
    r: parseInt(result[1]!, 16),
    g: parseInt(result[2]!, 16),
    b: parseInt(result[3]!, 16),
  };
}

function createParticle(
  mode: 'converge' | 'burst' | 'ambient',
  w: number,
  h: number,
  color: string,
): Particle {
  const cx = w / 2;
  const cy = h / 2;

  if (mode === 'converge') {
    const side = Math.floor(Math.random() * 4);
    let x: number, y: number;
    switch (side) {
      case 0: x = Math.random() * w; y = -10; break;
      case 1: x = w + 10; y = Math.random() * h; break;
      case 2: x = Math.random() * w; y = h + 10; break;
      default: x = -10; y = Math.random() * h; break;
    }
    const life = 60 + Math.random() * 60;
    return {
      x, y,
      targetX: cx + (Math.random() - 0.5) * 40,
      targetY: cy + (Math.random() - 0.5) * 40,
      vx: 0, vy: 0,
      size: 1 + Math.random() * 3,
      alpha: 0.3 + Math.random() * 0.7,
      life,
      maxLife: life,
      color,
    };
  }

  if (mode === 'burst') {
    const angle = Math.random() * Math.PI * 2;
    const speed = 2 + Math.random() * 6;
    const life = 30 + Math.random() * 40;
    return {
      x: cx + (Math.random() - 0.5) * 20,
      y: cy + (Math.random() - 0.5) * 20,
      targetX: 0, targetY: 0,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      size: 1.5 + Math.random() * 2.5,
      alpha: 0.6 + Math.random() * 0.4,
      life,
      maxLife: life,
      color,
    };
  }

  // ambient
  const life = 120 + Math.random() * 120;
  return {
    x: Math.random() * w,
    y: Math.random() * h,
    targetX: 0, targetY: 0,
    vx: (Math.random() - 0.5) * 0.5,
    vy: (Math.random() - 0.5) * 0.5,
    size: 1 + Math.random() * 2,
    alpha: 0.1 + Math.random() * 0.3,
    life,
    maxLife: life,
    color,
  };
}

export function SummonParticles({
  color,
  active,
  mode,
  intensity = 0.5,
}: SummonParticlesProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animFrameRef = useRef<number>(0);
  const particlesRef = useRef<Particle[]>([]);
  const activeRef = useRef(active);
  activeRef.current = active;

  const spawnRate = useCallback(() => {
    const base = mode === 'ambient' ? 1 : mode === 'converge' ? 4 : 8;
    return Math.max(1, Math.round(base * intensity));
  }, [mode, intensity]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const updateSize = () => {
      const parent = canvas.parentElement;
      if (parent) {
        canvas.width = parent.clientWidth;
        canvas.height = parent.clientHeight;
      } else {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
      }
    };
    updateSize();

    const resizeObserver = new ResizeObserver(updateSize);
    const parent = canvas.parentElement;
    if (parent) {
      resizeObserver.observe(parent);
    }

    const rgb = hexToRgb(color);

    const animate = () => {
      const w = canvas.width;
      const h = canvas.height;

      ctx.clearRect(0, 0, w, h);
      ctx.globalCompositeOperation = 'lighter';

      // Spawn new particles if active
      if (activeRef.current && particlesRef.current.length < MAX_PARTICLES) {
        const count = spawnRate();
        for (let i = 0; i < count; i++) {
          if (particlesRef.current.length >= MAX_PARTICLES) break;
          particlesRef.current.push(createParticle(mode, w, h, color));
        }
      }

      // Update and draw
      const alive: Particle[] = [];
      for (const p of particlesRef.current) {
        p.life--;

        if (mode === 'converge') {
          const dx = p.targetX - p.x;
          const dy = p.targetY - p.y;
          p.vx += dx * 0.02;
          p.vy += dy * 0.02;
          p.vx *= 0.96;
          p.vy *= 0.96;
        } else if (mode === 'burst') {
          p.vx *= 0.97;
          p.vy *= 0.97;
        }
        // ambient uses constant velocity

        p.x += p.vx;
        p.y += p.vy;

        const lifeRatio = p.life / p.maxLife;
        const fadeAlpha = mode === 'converge'
          ? p.alpha * Math.min(1, lifeRatio * 3) * Math.min(1, (1 - lifeRatio) * 3)
          : p.alpha * lifeRatio;

        if (p.life > 0 && fadeAlpha > 0.01) {
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${rgb.r},${rgb.g},${rgb.b},${fadeAlpha})`;
          ctx.fill();
          alive.push(p);
        }
      }

      particlesRef.current = alive;
      animFrameRef.current = requestAnimationFrame(animate);
    };

    animFrameRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animFrameRef.current);
      resizeObserver.disconnect();
      particlesRef.current = [];
    };
  }, [color, mode, spawnRate]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        width: '100%',
        height: '100%',
      }}
    />
  );
}
